/**
 * IconList.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useState } from "react";
import { IIconListPack } from "./IconListTypes";
import { IconListContext } from "./IconListContext";
import { BEM, IIconPackageURLMapping } from "@adapptio/lib-components";
import { ICON_SIZE } from "../Icon";
import { IconListTabs } from "./IconListTabs";
import { useDeepCompareMemo } from "use-deep-compare";
import { matchAndSort } from "@adapptio/lib-shared";
import { IconListValue } from "./IconListValue";

import "./IconList.scss";

export interface IIconListProps {
	iconPacks: IIconListPack[];
	urlMapping?: IIconPackageURLMapping;
	iconPreviewSize?: ICON_SIZE;
	selectedPackName?: string;
	selectedIconName?: string;
	className?: string;
	onSelect?: (packName: string, iconName: string) => void;
}

export const IconList: React.FC<IIconListProps> = ({
	iconPacks,
	className,
	selectedPackName,
	selectedIconName,
	urlMapping,
	iconPreviewSize,
	onSelect
}) => {
	const baseClass = BEM("edt-icon-list");
	const [ search, setSearch ] = useState("");
	const searchValue = search.toLowerCase();

	const filteredIconPacks = useDeepCompareMemo(() => {
		if (searchValue) {
			return iconPacks.map((iconPack) => {
				const icons = matchAndSort(iconPack.icons, searchValue, {
					keys: [ "name", "title", "tags" ]
				});

				return {
					...iconPack,
					icons
				};
			});
		}

		return iconPacks;
	}, [ iconPacks, searchValue ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<IconListContext.Provider
			value={{
				search,
				setSearch,
				urlMapping,
				iconPreviewSize,
				selectedIconName,
				selectedPackName,
				onIconSelect: onSelect
			}}
		>
			<div className={baseClass.toClassName()}>
				<IconListValue />

				<IconListTabs iconPacks={filteredIconPacks} />
			</div>
		</IconListContext.Provider>
	);
};

IconList.defaultProps = {
	iconPreviewSize: ICON_SIZE.MEDIUM
};
